import cn from 'classnames'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React, { ReactElement } from 'react'

import style from './UsageMeterCard.style'

const CRITICAL_THRESHOLD = 90

interface UsageMeterCardProps {
  title: string
  usage: number
  limit: number
  limitSuffix?: string
  sublabel?: string
  footerSuffix?: string
  tooltipContent: string
  ctaLabel?: string
  onCtaClick?: () => void
  onTooltipHover?: () => void
}

export default function UsageMeterCard(
  props: UsageMeterCardProps,
): ReactElement {
  const {
    title,
    usage,
    limit,
    limitSuffix,
    sublabel,
    footerSuffix,
    tooltipContent,
    ctaLabel,
    onCtaClick,
    onTooltipHover,
  } = props

  const percentage = limit > 0 ? Math.round((usage / limit) * 100) : 0
  const fillPercent = limit > 0 ? Math.min(100, (usage / limit) * 100) : 0
  const isCritical = percentage >= CRITICAL_THRESHOLD

  const tooltipText = isCritical
    ? tooltipContent
    : [tooltipContent, sublabel, footerSuffix].filter(Boolean).join(' ')

  return (
    <div
      className={cn('UsageMeterCard', { 'is-critical': isCritical })}
      css={style}
      data-testid="usage-meter-card"
    >
      <div className="UsageMeterCard-header">
        <span className="UsageMeterCard-title">{title}</span>
        <span onMouseEnter={onTooltipHover}>
          <TooltipIcon iconTestId="usage-meter-card-tooltip" placement="top">
            {tooltipText}
          </TooltipIcon>
        </span>
      </div>
      <div className="UsageMeterCard-count-row">
        <span
          className="UsageMeterCard-count"
          data-testid="usage-meter-card-count"
        >
          {usage}
        </span>
        <span className="UsageMeterCard-limit">
          {'/ '}
          {limit}
          {limitSuffix ? ` ${limitSuffix}` : ''}
        </span>
      </div>
      <div
        className="UsageMeterCard-bar"
        role="meter"
        aria-valuenow={usage}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-label={title}
      >
        <div
          className="UsageMeterCard-bar-fill"
          style={{ width: `${fillPercent}%` }}
        />
      </div>
      {isCritical && (
        <div className="UsageMeterCard-footer">
          <span className="UsageMeterCard-sublabel">
            {[sublabel, `${percentage}% of limit`, footerSuffix]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {ctaLabel && onCtaClick && (
            <button
              type="button"
              className="UsageMeterCard-cta"
              onClick={onCtaClick}
              data-testid="usage-meter-card-cta"
            >
              {ctaLabel}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
